/**
 * LE PROFIL DE MENU PAR RÔLE — décision de Dove du 15/09 (D1, D2), corrigée le
 * 16/09 en relecture finale (F1/F2) pour la permission `arbitrage`.
 *
 * Élodie (secretariat + logistique) voyait 16 entrées, dont Pennylane, Finance
 * et Encours ancien parce que `secretariat ∈ ROLES_FINANCE`. Elle garde la file
 * Finance d'À relancer ; les trois PAGES sortent de son menu. Rien ici ne touche
 * un droit : `rolesRequis`, `canAccess` et les gardes serveur ne bougent pas —
 * une page exclue du menu reste atteignable par son adresse si le rôle y a droit.
 *
 * Les libellés des groupes (« Ma journée · Qui · Parler ») sont ceux de la
 * maquette validée ; `NAV_GROUP_LABELS` reste pour le menu complet.
 *
 * `null` = menu complet. Un compte qui porte UN rôle complet a le menu complet
 * (Dove : super_admin + admin).
 *
 * LE MÉCANISME EST PAR RÔLE, RAFFINÉ PAR LA PERMISSION `arbitrage` — pas
 * l'inverse. Estelle porte `secretariat` (comme Élodie) mais AUSSI la
 * permission `arbitrage` (spec C1 : qui arbitre voit tout). Un rollback « une
 * ligne » qui aurait remis `secretariat` en menu complet dans `MENU_PAR_ROLE`
 * aurait DÉ-réduit Élodie aussi : le rôle est PARTAGÉ entre les deux comptes,
 * la permission ne l'est pas. C'est pourquoi la permission est un paramètre à
 * part, jamais une variante du tableau par rôle.
 */
import type { Permission, Role } from "./roles";

export interface GroupeMenu {
  libelle: string;
  /** ids de modules (`ModuleDef.id`, côté web), dans l'ordre d'affichage */
  modules: readonly string[];
}

export interface ProfilMenu {
  groupes: readonly GroupeMenu[];
  /** ids retirés du menu (ni visibles, ni sous « Plus ») — jamais des droits */
  exclus: readonly string[];
}

const SECRETARIAT_LOGISTIQUE: ProfilMenu = {
  groupes: [
    { libelle: "Ma journée", modules: ["travaux-du-jour", "relances"] },
    { libelle: "Qui", modules: ["organisations", "praticiens"] },
    { libelle: "Parler", modules: ["emails", "appels"] },
  ],
  exclus: ["pennylane", "finance", "encours-ancien"],
};

export const MENU_PAR_ROLE: Readonly<Record<Role, ProfilMenu | null>> = {
  super_admin: null,
  admin: null,
  secretariat: SECRETARIAT_LOGISTIQUE,
  logistique: SECRETARIAT_LOGISTIQUE,
  comptabilite: null,
  commercial: null,
  production: null,
  technicien: null,
  lecture: null,
};

export function modulesVisibles(p: ProfilMenu): string[] {
  return p.groupes.flatMap((g) => [...g.modules]);
}

/**
 * Le profil d'une personne. Un rôle complet l'emporte : réduire le menu de
 * quelqu'un qui a AUSSI un rôle complet lui cacherait des pages qu'il utilise.
 * Deux rôles réduits différents : le premier trouvé (aujourd'hui, ils sont
 * identiques — le jour où ils divergent, ce choix devient une décision).
 *
 * `permissions` (spec C1) : la personne qui ARBITRE voit tout, quel que soit
 * son rôle — Estelle porte `secretariat` comme Élodie, mais AUSSI `arbitrage`.
 * Vérifiée avant le rôle : elle prime sur n'importe quelle réduction.
 */
export function profilMenu(roles: readonly Role[], permissions: readonly Permission[] = []): ProfilMenu | null {
  if (permissions.includes("arbitrage")) return null;
  if (roles.length === 0) return null;
  const profils = roles.map((r) => MENU_PAR_ROLE[r]);
  if (profils.some((p) => p === null)) return null;
  return profils[0] ?? null;
}
